import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useContext, useMemo } from 'react';
import { Icon, LocaleContext, Spinner, Text, useLocalizeMessage } from 'libs.nucleus';
import { COLORS_HEX, NumberChart, ReportingChart } from 'libs.react';
import { TableChart } from '../../../data_reports/components/report_chart/report_chart_types/table_chart.component';
import { buildChartLegendOptions } from '../../../data_reports/utils/chart_legend.utils';
import { formatCubeValue, isNumericCubeFormat } from '../../../data_reports/utils/cube_format.utils';
import { buildNumberChartDatasets } from '../../../data_reports/utils/number_chart.utils';
import { getDynamicStyles } from '../../../data_reports/utils/style_helpers.utils';
import { getPersistedChartType, resolveChartFieldDisplayName, sanitizeConditionalFormatsForPalette } from '../../utils';
// =============================================================================
// Main Component
// =============================================================================
const ChartPreviewContentInner = ({ chartType, resultSet, isLoading, error, total, onPageChange, tablePaginationState, setTableSortingConfig, labelOverrides, conditionalFormats, orientation, pivotConfig, palette, xAxis, yAxis, splitBy, xTitle, yTitle, showLabels, showLegend, showArea, showDots, numberValues, dotSizeMapping, dotSizeField, dotSizeConstant, normalizeBubbleSize, bubbleSizeMinOverride, bubbleSizeMaxOverride, defaultSortedColumns, }) => {
    const translate = useLocalizeMessage();
    // ── Loading state (only for initial load before any data arrives) ─────
    if (isLoading && !resultSet) {
        return (_jsx("div", { className: 'flex items-center justify-center w-full h-64', children: _jsx(Spinner, {}) }));
    }
    // ── Error state ─────────────────────────────────────────────────────────
    if (error) {
        return _jsx(ErrorState, { message: error.message });
    }
    // ── No data state (query ran but no results) ───────────────────────────
    if (!resultSet) {
        return _jsx(EmptyState, { message: translate('No data available. Try adjusting your query parameters.') });
    }
    // ── Render chart by type ────────────────────────────────────────────────
    switch (chartType) {
        case 'table':
            return (_jsx(TableChart, { resultSets: [resultSet], style: buildStyleFromConditionalFormats(sanitizeConditionalFormatsForPalette(conditionalFormats, palette)), setSortingConfig: setTableSortingConfig, isLoading: isLoading, labelOverrides: labelOverrides, defaultSortedColumns: defaultSortedColumns, serverSidePagination: {
                    total,
                    paginationState: tablePaginationState,
                    onPaginationChange: (state) => onPageChange?.(state.pageIndex, state.pageSize),
                } }));
        case 'bar':
        case 'groupedBar':
        case 'stackedBar':
        case 'horizontalBar':
            return (_jsx(DynamicChartPreview, { type: getPersistedChartType({ chartType, orientation }), builderChartType: chartType, resultSet: resultSet, pivotConfig: pivotConfig, palette: palette, conditionalFormats: conditionalFormats, xTitle: xTitle, yTitle: yTitle }));
        case 'scatter':
            return (_jsx(ScatterChartPreview, { resultSet: resultSet, palette: palette, conditionalFormats: conditionalFormats, xTitle: xTitle, yTitle: yTitle, showLegend: showLegend, xAxis: xAxis, yAxis: yAxis, splitBy: splitBy, dotSizeMapping: dotSizeMapping, dotSizeField: dotSizeField, dotSizeConstant: dotSizeConstant, normalizeBubbleSize: normalizeBubbleSize, bubbleSizeMinOverride: bubbleSizeMinOverride, bubbleSizeMaxOverride: bubbleSizeMaxOverride }));
        case 'line':
        case 'multiLine':
        case 'multivariate':
            return (_jsx(DynamicChartPreview, { type: 'line', builderChartType: chartType, resultSet: resultSet, pivotConfig: pivotConfig, palette: palette, conditionalFormats: conditionalFormats, xTitle: xTitle, yTitle: yTitle, showArea: showArea, showDots: showDots }));
        case 'pie':
            return (_jsx(DynamicChartPreview, { type: 'pie', builderChartType: chartType, resultSet: resultSet, pivotConfig: pivotConfig, palette: palette, showLabels: showLabels, showLegend: showLegend }));
        case 'number':
            return (_jsx(NumberPreview, { resultSet: resultSet, pivotConfig: pivotConfig, palette: palette, conditionalFormats: conditionalFormats, showLabels: showLabels, showLegend: showLegend, numberValues: numberValues }));
        default:
            return _jsx(EmptyState, { message: translate('Chart preview will appear here after running the query.') });
    }
};
// Memoised so that keystrokes in the settings panel (which re-render the
// builder) don't walk the preview tree when none of the rendering-relevant
// props actually changed. The parent passes a snapshot of settings taken at
// Run time, so shallow equality is enough.
export const ChartPreviewContent = memo(ChartPreviewContentInner);
// =============================================================================
// Empty State
// =============================================================================
const EmptyState = ({ message }) => (_jsx("div", { className: 'flex flex-col items-center justify-center w-full h-64 text-neutral-400 gap-2', children: _jsx(Text, { size: 'sm', children: message }) }));
// =============================================================================
// Error State
// =============================================================================
const ErrorState = ({ message }) => {
    const translate = useLocalizeMessage();
    return (_jsxs("div", { className: 'flex flex-col items-center justify-center w-full h-64 text-red-500 gap-2', children: [_jsx(Icon, { name: 'error', size: 'base', color: 'current' }), _jsx(Text, { size: 'sm', className: 'font-medium', children: translate('Error loading chart data') }), _jsx(Text, { size: 'xs', className: 'text-neutral-500 max-w-md text-center', children: message })] }));
};
const PALETTE_COLOR_KEYS = {
    neutral: ['neutral'],
    categorical: [
        'red',
        'redDark',
        'orange',
        'orangeDark',
        'yellow',
        'green',
        'blue',
        'blueDark',
        'purple',
        'purpleDark',
    ],
    purple: ['purple1', 'purple2', 'purple3', 'purple4', 'purple5'],
    greyscale: ['neutral', 'lightGrey', 'grey', 'darkGrey', 'black'],
};
const resolveChartColor = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => COLORS_HEX[item] || item);
    }
    if (!value) {
        return undefined;
    }
    return COLORS_HEX[value] || value;
};
/** Empty conditional color tokens must fall back to the series palette color (arrays are truthy in JS). */
const coalesceResolvedColorWithDefault = (resolved, defaultColor) => {
    if (resolved === undefined) {
        return defaultColor;
    }
    if (Array.isArray(resolved)) {
        return resolved.map((c) => (c && String(c).trim() !== '' ? c : defaultColor));
    }
    return resolved.trim() === '' ? defaultColor : resolved;
};
export const resolveColorToken = (token) => token ? COLORS_HEX[token] || token : token;
// Merge rules that share a column into a single style entry. The settings
// panel now stores one rule per visual row (to keep row order stable), but
// `getColumnStyle` downstream picks a column style via `Array.find`, so any
// duplicate `columnName` after the first would be ignored. Concatenating the
// conditions in first-occurrence order preserves both evaluation semantics
// and the priority order the user set up.
export const buildStyleFromConditionalFormats = (formats) => {
    if (!formats || formats.length === 0) {
        return undefined;
    }
    const styleByColumn = new Map();
    for (const rule of formats) {
        const key = rule.columnName ?? '';
        const resolvedConditions = rule.conditions.map((cond) => ({
            ...cond,
            result: resolveColorToken(cond.result) ?? cond.result,
        }));
        const existing = styleByColumn.get(key);
        if (existing) {
            existing.conditions = [
                ...(existing.conditions ?? []),
                ...(resolvedConditions ?? []),
            ];
        }
        else {
            styleByColumn.set(key, {
                columnName: rule.columnName,
                property: rule.property,
                type: rule.type,
                showValue: rule.showValue,
                conditions: resolvedConditions,
                defaultValue: resolveColorToken(rule.defaultValue) ?? rule.defaultValue,
            });
        }
    }
    return Array.from(styleByColumn.values());
};
const getPaletteColor = (palette, index) => {
    const paletteKey = palette || 'categorical';
    const colorKey = PALETTE_COLOR_KEYS[paletteKey][index % PALETTE_COLOR_KEYS[paletteKey].length];
    return COLORS_HEX[colorKey] || colorKey;
};
const PIE_DATA_LABELS_PLUGIN = {
    id: 'pieDataLabels',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    afterDraw(chart) {
        if (!chart.options?.plugins?.pieDataLabels?.enabled) {
            return;
        }
        const meta = chart.getDatasetMeta(0);
        if (!meta?.data?.length) {
            return;
        }
        const { ctx } = chart;
        meta.data.forEach((element, index) => {
            const value = chart.data.datasets[0]?.data[index];
            if (!value) {
                return;
            }
            const { x, y } = element.getCenterPoint();
            ctx.save();
            ctx.fillStyle = 'white';
            ctx.font = 'bold 14px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(value), x, y);
            ctx.restore();
        });
    },
};
const PIE_PLUGINS = [PIE_DATA_LABELS_PLUGIN];
/**
 * Builds a chart.js tooltip `label` callback that renders each measure's Cube `format`
 * (percent/abbr/etc.) so the builder preview matches the published chart and the table (SSP-3918).
 * Falls back to chart.js's own `formattedValue` when the measure has no numeric Cube format, so
 * unformatted charts keep their default (locale-separated) output.
 */
const buildCubeFormatTooltip = (resultSet, pivotConfig, locale, isPie, isHorizontal) => {
    const { measures } = resultSet.annotation();
    const measureKeys = Object.keys(measures || {});
    // Series keys may be the bare measure name or a pivoted compound like "US,cube.measure"; match by suffix.
    const seriesKeys = resultSet.series(pivotConfig).map((series) => series.key);
    const resolveFormat = (datasetIndex) => {
        const seriesKey = seriesKeys[datasetIndex];
        if (!seriesKey) {
            return undefined;
        }
        const direct = measures?.[seriesKey];
        if (direct) {
            return direct.format;
        }
        const matchKey = measureKeys.find((key) => seriesKey.endsWith(key));
        return matchKey ? measures[matchKey].format : undefined;
    };
    return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        label: (context) => {
            const cubeFormat = resolveFormat(context.datasetIndex);
            // Pie slices expose the value on `parsed` directly; vertical bar/line use `parsed.y`,
            // while horizontal bars (indexAxis: 'y') put the measure value on `parsed.x`.
            const rawValue = isPie ? context.parsed : isHorizontal ? context.parsed?.x : context.parsed?.y;
            const valueStr = isNumericCubeFormat(cubeFormat)
                ? formatCubeValue(rawValue, cubeFormat, locale)
                : context.formattedValue;
            // Pie tooltips are prefixed by the slice label; bar/line by the dataset (series) label.
            const prefix = isPie ? context.label : context.dataset?.label;
            return prefix ? `${prefix}: ${valueStr}` : valueStr;
        },
    };
};
const DynamicChartPreview = ({ type, builderChartType, resultSet, pivotConfig, palette, conditionalFormats, xTitle, yTitle, showLabels, showLegend, showArea, showDots, }) => {
    const locale = useContext(LocaleContext).toString();
    const style = useMemo(() => buildStyleFromConditionalFormats(sanitizeConditionalFormatsForPalette(conditionalFormats, palette)), [conditionalFormats, palette]);
    const data = useMemo(() => {
        const chartPivot = resultSet.chartPivot(pivotConfig);
        const seriesData = resultSet.series(pivotConfig);
        if (type === 'pie') {
            const labels = chartPivot.map((row) => row.x);
            const firstSeries = seriesData[0];
            const values = firstSeries?.series.map(({ value }) => value) || [];
            const colors = values.map((_, idx) => getPaletteColor(palette, idx));
            return {
                labels,
                datasets: [
                    {
                        label: firstSeries?.shortTitle || firstSeries?.title || '',
                        data: values,
                        backgroundColor: colors,
                    },
                ],
            };
        }
        return {
            labels: chartPivot.map((row) => row.x),
            datasets: seriesData.map((series, idx) => {
                const defaultColor = getPaletteColor(palette, idx);
                const dynamicStyles = style ? getDynamicStyles(series, style) : {};
                const { backgroundColor: _bg, borderColor: _bc, ...restDynamicStyles } = dynamicStyles;
                const resolvedBackgroundColor = coalesceResolvedColorWithDefault(resolveChartColor(dynamicStyles.backgroundColor), defaultColor);
                const isLine = type === 'line';
                return {
                    label: series.shortTitle || series.title,
                    data: series.series.map(({ value }) => value),
                    backgroundColor: resolvedBackgroundColor,
                    borderColor: isLine
                        ? coalesceResolvedColorWithDefault(resolveChartColor(dynamicStyles.borderColor), defaultColor)
                        : undefined,
                    ...(isLine && { fill: !!showArea }),
                    ...(isLine && !showDots && { pointRadius: 0 }),
                    ...restDynamicStyles,
                };
            }),
        };
    }, [conditionalFormats, palette, pivotConfig, resultSet, showArea, showDots, style, type]);
    const options = useMemo(() => {
        const isPie = type === 'pie';
        const isHorizontal = type === 'horizontalBar';
        const isStacked = type === 'stackedBar' || (isHorizontal && builderChartType === 'stackedBar');
        const tooltip = { callbacks: buildCubeFormatTooltip(resultSet, pivotConfig, locale, isPie, isHorizontal) };
        if (isPie) {
            return {
                plugins: {
                    legend: buildChartLegendOptions({ chartType: 'pie', showLegend }),
                    pieDataLabels: {
                        enabled: !!showLabels,
                    },
                    tooltip,
                },
            };
        }
        // When horizontal, the chart's x-axis shows values and y-axis shows categories,
        // so the user's conceptual axis titles need to swap.
        const renderedXTitle = isHorizontal ? yTitle : xTitle;
        const renderedYTitle = isHorizontal ? xTitle : yTitle;
        // Format the value axis ticks with the measure's Cube format so axis labels match the tooltip
        // and table (SSP-3972): e.g. 0.96 -> "96%", 12000 -> "12K". The value axis is X for horizontal
        // bars (indexAxis: 'y') and Y otherwise; the category axis keeps its raw labels.
        const { measures } = resultSet.annotation();
        const firstMeasureKey = Object.keys(measures || {})[0];
        const valueAxisFormat = measures?.[firstMeasureKey]?.format;
        const valueAxis = isHorizontal ? 'x' : 'y';
        const measureTicks = isNumericCubeFormat(valueAxisFormat)
            ? { ticks: { callback: (value) => formatCubeValue(value, valueAxisFormat, locale) } }
            : undefined;
        // Chart.js scale config is loosely typed here (mirrors the published-chart getAxisOptions).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let scales;
        if (renderedXTitle || renderedYTitle) {
            scales = {
                x: {
                    ...(isStacked ? { stacked: true } : {}),
                    title: renderedXTitle ? { display: true, text: renderedXTitle } : undefined,
                },
                y: {
                    ...(isStacked ? { stacked: true } : {}),
                    title: renderedYTitle ? { display: true, text: renderedYTitle } : undefined,
                },
            };
        }
        else if (isHorizontal) {
            scales = { x: { stacked: isStacked }, y: { stacked: isStacked } };
        }
        else if (type === 'stackedBar') {
            scales = { x: { stacked: true }, y: { stacked: true } };
        }
        if (measureTicks) {
            scales = scales || {};
            scales[valueAxis] = { ...(scales[valueAxis] || {}), ...measureTicks };
        }
        const scaleOptions = scales ? { scales } : {};
        return {
            ...scaleOptions,
            plugins: {
                tooltip,
                legend: buildChartLegendOptions({ chartType: type, builderChartType, showLegend }),
            },
        };
    }, [builderChartType, locale, pivotConfig, resultSet, showLabels, showLegend, type, xTitle, yTitle]);
    const plugins = type === 'pie' ? PIE_PLUGINS : undefined;
    return _jsx(ReportingChart, { type: type, data: data, options: options, plugins: plugins });
};
const DEFAULT_SCATTER_POINT_RADIUS = 8;
const ScatterChartPreview = ({ resultSet, palette, conditionalFormats, xTitle, yTitle, showLegend, xAxis, yAxis, splitBy, dotSizeMapping, dotSizeField, dotSizeConstant, normalizeBubbleSize, bubbleSizeMinOverride, bubbleSizeMaxOverride, }) => {
    const locale = useContext(LocaleContext).toString();
    const style = useMemo(() => buildStyleFromConditionalFormats(sanitizeConditionalFormatsForPalette(conditionalFormats, palette)), [conditionalFormats, palette]);
    const isBubble = dotSizeMapping === 'variable' && !!dotSizeField?.key;
    const constantRadius = !isBubble && dotSizeConstant != null ? dotSizeConstant : undefined;
    const data = useMemo(() => {
        if (!xAxis?.key || !yAxis?.key) {
            return { datasets: [] };
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rows = (resultSet.rawData() || []);
        const baseRadius = constantRadius ?? DEFAULT_SCATTER_POINT_RADIUS;
        // Pre-compute normalisation bounds across the entire dataset so all
        // splitBy groups share the same scale.
        let normMin = 0;
        let normMax = 0;
        if (isBubble && normalizeBubbleSize && dotSizeField?.key) {
            const allSizeValues = rows.map((row) => Number(row[dotSizeField.key])).filter((n) => !isNaN(n));
            const dataMin = allSizeValues.length > 0 ? Math.min(...allSizeValues) : 0;
            const dataMax = allSizeValues.length > 0 ? Math.max(...allSizeValues) : 0;
            normMin = bubbleSizeMinOverride ?? dataMin;
            normMax = bubbleSizeMaxOverride ?? dataMax;
        }
        const MIN_NORMALISED_RADIUS = 4;
        const MAX_NORMALISED_RADIUS = 40;
        const groups = new Map();
        rows.forEach((row) => {
            const key = splitBy?.key ? String(row[splitBy.key] ?? '') : '';
            const list = groups.get(key);
            if (list) {
                list.push(row);
            }
            else {
                groups.set(key, [row]);
            }
        });
        const datasets = Array.from(groups.entries()).map(([groupLabel, groupRows], idx) => {
            const defaultColor = getPaletteColor(palette, idx);
            const dynamicStyles = style ? getDynamicStyles({ key: groupLabel }, style) : {};
            const { backgroundColor: _bg, ...restDynamicStyles } = dynamicStyles;
            const resolvedBackgroundColor = coalesceResolvedColorWithDefault(resolveChartColor(dynamicStyles.backgroundColor), defaultColor);
            const dataPoints = groupRows.map((row) => ({
                x: Number(row[xAxis.key]) || 0,
                y: Number(row[yAxis.key]) || 0,
            }));
            let pointRadii;
            let rawSizeValues;
            if (isBubble && dotSizeField?.key) {
                const rawRadii = groupRows.map((row) => {
                    const num = Number(row[dotSizeField.key]);
                    return isNaN(num) || num <= 0 ? baseRadius : num;
                });
                rawSizeValues = rawRadii;
                if (normalizeBubbleSize && normMax !== normMin) {
                    pointRadii = rawRadii.map((r) => {
                        const clamped = Math.max(normMin, Math.min(normMax, r));
                        return (MIN_NORMALISED_RADIUS +
                            ((clamped - normMin) / (normMax - normMin)) * (MAX_NORMALISED_RADIUS - MIN_NORMALISED_RADIUS));
                    });
                }
                else if (normalizeBubbleSize && normMax === normMin) {
                    pointRadii = rawRadii.map(() => (MIN_NORMALISED_RADIUS + MAX_NORMALISED_RADIUS) / 2);
                }
                else {
                    pointRadii = rawRadii;
                }
            }
            else {
                pointRadii = baseRadius;
            }
            const hoverRadii = Array.isArray(pointRadii) ? pointRadii.map((r) => r + 2) : baseRadius + 2;
            return {
                label: groupLabel || yAxis.name || yAxis.key,
                data: dataPoints,
                backgroundColor: resolvedBackgroundColor,
                pointRadius: pointRadii,
                pointHoverRadius: hoverRadii,
                _rawSizeValues: rawSizeValues,
                ...restDynamicStyles,
            };
        });
        return { datasets };
    }, [
        constantRadius,
        dotSizeField?.key,
        isBubble,
        normalizeBubbleSize,
        bubbleSizeMinOverride,
        bubbleSizeMaxOverride,
        palette,
        resultSet,
        splitBy?.key,
        style,
        xAxis?.key,
        yAxis?.key,
        yAxis?.name,
    ]);
    const options = useMemo(() => {
        // Tooltip callback (FR3): name each involved measure (X, Y, and dot-size in
        // bubble mode) in the point tooltip so users see every contributing value.
        // Prefer Cube annotation / dataset display names over bare member keys (SSP-4278).
        const annotation = resultSet.annotation();
        const xLabel = resolveChartFieldDisplayName({
            fieldKey: xAxis?.key,
            fieldName: xAxis?.name,
            annotation,
            fallback: xTitle,
        });
        const yLabel = resolveChartFieldDisplayName({
            fieldKey: yAxis?.key,
            fieldName: yAxis?.name,
            annotation,
            fallback: yTitle,
        });
        const sizeLabel = isBubble
            ? resolveChartFieldDisplayName({
                fieldKey: dotSizeField?.key,
                fieldName: dotSizeField?.name,
                annotation,
            })
            : undefined;
        // Each axis/size field carries its own Cube format; X may be a dimension or a measure,
        // Y and dot-size are measures. Resolve from the load annotation so the scatter tooltip
        // renders the same units (%, K/M) as the table and other charts (SSP-3918).
        const formatForKey = (key) => key ? (annotation.measures?.[key]?.format ?? annotation.dimensions?.[key]?.format) : undefined;
        const xFormat = formatForKey(xAxis?.key);
        const yFormat = formatForKey(yAxis?.key);
        const sizeFormat = formatForKey(dotSizeField?.key);
        const formatValue = (value, format) => isNumericCubeFormat(format) ? formatCubeValue(value, format, locale) : `${value}`;
        return {
            plugins: {
                legend: buildChartLegendOptions({ chartType: 'scatter', showLegend }),
                tooltip: {
                    callbacks: {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        label: (context) => {
                            const datasetLabel = context.dataset?.label ? `${context.dataset.label}: ` : '';
                            const parsed = context.parsed || {};
                            const raw = context.raw || {};
                            const parts = [
                                `${xLabel}: ${formatValue(parsed.x ?? raw.x, xFormat)}`,
                                `${yLabel}: ${formatValue(parsed.y ?? raw.y, yFormat)}`,
                            ];
                            if (sizeLabel) {
                                const rawValues = context.dataset?._rawSizeValues;
                                const sizeValue = Array.isArray(rawValues)
                                    ? rawValues[context.dataIndex]
                                    : Array.isArray(context.dataset?.pointRadius)
                                        ? context.dataset.pointRadius[context.dataIndex]
                                        : context.dataset?.pointRadius;
                                if (sizeValue !== undefined) {
                                    parts.push(`${sizeLabel}: ${formatValue(sizeValue, sizeFormat)}`);
                                }
                            }
                            return `${datasetLabel}${parts.join(', ')}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    title: xTitle ? { display: true, text: xTitle } : undefined,
                    // Match the tooltip units (%, K/M) on the axis ticks (SSP-3972).
                    ...(isNumericCubeFormat(xFormat)
                        ? { ticks: { callback: (value) => formatCubeValue(value, xFormat, locale) } }
                        : {}),
                },
                y: {
                    title: yTitle ? { display: true, text: yTitle } : undefined,
                    ...(isNumericCubeFormat(yFormat)
                        ? { ticks: { callback: (value) => formatCubeValue(value, yFormat, locale) } }
                        : {}),
                },
            },
        };
    }, [
        dotSizeField?.key,
        dotSizeField?.name,
        isBubble,
        locale,
        resultSet,
        showLegend,
        xAxis?.key,
        xAxis?.name,
        xTitle,
        yAxis?.key,
        yAxis?.name,
        yTitle,
    ]);
    return _jsx(ReportingChart, { type: 'scatter', data: data, options: options });
};
const NumberPreview = ({ resultSet, pivotConfig, palette, conditionalFormats, showLabels, showLegend, numberValues, }) => {
    // Keep color tokens (do not resolve to hex) so BaseNumberChart can map them via
    // COLORS_TEXT. Hex values from Neutral (#3A3B3D) still work as inline CSS.
    const style = useMemo(() => {
        const sanitized = sanitizeConditionalFormatsForPalette(conditionalFormats, palette);
        if (!sanitized || sanitized.length === 0) {
            return undefined;
        }
        const styleByColumn = new Map();
        for (const rule of sanitized) {
            const key = rule.columnName ?? '';
            const existing = styleByColumn.get(key);
            if (existing) {
                existing.conditions = [...(existing.conditions ?? []), ...(rule.conditions ?? [])];
            }
            else {
                styleByColumn.set(key, {
                    columnName: rule.columnName,
                    property: rule.property,
                    type: rule.type,
                    conditions: [...rule.conditions],
                    defaultValue: rule.defaultValue,
                });
            }
        }
        return Array.from(styleByColumn.values());
    }, [conditionalFormats, palette]);
    const data = useMemo(() => {
        const datasets = buildNumberChartDatasets({
            resultSets: [resultSet],
            pivotConfig,
            style,
            palette,
            numberValues,
            showLabels,
            showLegend,
        });
        return { datasets };
    }, [numberValues, palette, pivotConfig, resultSet, showLabels, showLegend, style]);
    return _jsx(NumberChart, { data: data });
};
